import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'

import { httpOk, GET, POST } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

const TODAY = format(new Date(), 'yyyy-MM-dd')

function categoryBadge(days) {
	if (days <= 7)   return { label: '1–7 kun',    bg: '#d1e7dd', color: '#0f5132' }
	if (days <= 14)  return { label: '7–14 kun',   bg: '#fff3cd', color: '#664d03' }
	if (days <= 30)  return { label: '14–30 kun',  bg: '#ffe5b4', color: '#7d4e00' }
	if (days <= 120) return { label: '30–120 kun', bg: '#f8d7da', color: '#842029' }
	return               { label: '120+ kun',   bg: '#343a40', color: '#ffffff' }
}

function DebtMonitoring() {
	const { t } = useTranslation()

	const [allData, setAllData] = useState([])
	const [search, setSearch] = useState('')
	const [activeClient, setActiveClient] = useState(null)
	const [notes, setNotes] = useState([])
	const [noteText, setNoteText] = useState('')
	const [nextContactDate, setNextContactDate] = useState(null)
	const [saving, setSaving] = useState(false)

	async function load() {
		const response = await GET('/report/debt-monitoring', {}, { loader: true })
		if (httpOk(response)) setAllData(response.data.data || [])
	}

	async function openClient(item) {
		setActiveClient(item)
		setNoteText('')
		setNextContactDate(null)
		const response = await GET('/report/debt-followups/' + item.client_id, {}, { loader: true })
		if (httpOk(response)) setNotes(response.data.data || [])
	}

	async function saveNote(e) {
		e.preventDefault()
		if (!noteText.trim() || !nextContactDate) return
		setSaving(true)
		const payload = {
			client_id: activeClient.client_id,
			note: noteText.trim(),
			next_contact_date: format(nextContactDate, 'yyyy-MM-dd'),
		}
		const response = await POST('/report/debt-followups', payload, { loader: false })
		if (httpOk(response)) {
			setNoteText('')
			setNextContactDate(null)
			const r = await GET('/report/debt-followups/' + activeClient.client_id, {}, { loader: false })
			if (httpOk(r)) setNotes(r.data.data || [])
			await load()
			setActiveClient(prev => {
				const updated = allData.find(d => d.client_id === prev.client_id)
				return updated ? { ...updated } : prev
			})
		}
		setSaving(false)
	}

	const filtered = useMemo(() => {
		if (!search.trim()) return allData
		const q = search.trim().toLowerCase()
		return allData.filter(i =>
			(i.client_name || '').toLowerCase().includes(q) ||
			(i.client_phone || '').includes(q)
		)
	}, [allData, search])

	useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">{t('debt_monitoring')}</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex align-items-center gap-2 mb-3">
						<input
							type="text"
							className="form-control"
							style={{ maxWidth: 260 }}
							placeholder={t('search') + '...'}
							value={search}
							onChange={e => setSearch(e.target.value)}
						/>
						<span className="text-muted fz-14 ms-auto">
							{t('total')}: <b>{filtered.length}</b>
						</span>
					</div>

					<div className="table-responsive" style={{ minHeight: 'calc(100vh - 220px)' }}>
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ width: 40 }} className="text-center">#</th>
									<th>{t('client')}</th>
									<th>{t('phone')}</th>
									<th className="text-end">{t('debt_amount')}</th>
									<th className="text-center" style={{ width: 130 }}>{t('days_in_debt')}</th>
									<th className="text-center" style={{ width: 120 }}>{t('next_contact_date')}</th>
									<th>{t('last_note')}</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((item, index) => {
									const days = item.days_in_debt ?? 0
									const badge = categoryBadge(days)
									const ncd = item.next_contact_date ? item.next_contact_date.slice(0, 10) : null
									const overdue = ncd && ncd < TODAY
									const isToday = ncd && ncd === TODAY
									let rowBg = ''
									if (overdue) rowBg = '#fff8f8'
									else if (isToday) rowBg = '#fffde7'
									return (
										<tr key={item.client_id} className="cursor"
											style={{ background: rowBg }}
											onClick={() => openClient(item)}>
											<td className="text-center">{index + 1}</td>
											<td className="fw-semibold">{item.client_name}</td>
											<td className="text-nowrap">{item.client_phone || '—'}</td>
											<td className="text-end text-nowrap text-danger fw-semibold">
												{formatMoney(item.debt_amount)}
											</td>
											<td className="text-center">
												<span className="badge rounded-pill px-2 py-1 fz-13"
													style={{ background: badge.bg, color: badge.color }}>
													{days} {t('days_short')}
												</span>
											</td>
											<td className="text-center text-nowrap">
												{ncd
													? <span style={{
														color: overdue ? '#dc3545' : isToday ? '#856404' : '#198754',
														fontWeight: (overdue || isToday) ? 700 : 400,
													}}>
														{formatDate(ncd, 'dd.MM.yyyy')}
														{overdue && <i className="uil uil-exclamation-triangle ms-1"></i>}
														{isToday && <i className="uil uil-bell ms-1"></i>}
													</span>
													: <span className="text-muted">—</span>
												}
											</td>
											<td>
												{item.last_note
													? <span className="fz-13 text-muted" style={{ display: 'block', maxWidth: 300, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
														{item.last_note_by &&
															<span className="fw-semibold text-secondary me-1">{item.last_note_by}:</span>
														}
														{item.last_note}
													</span>
													: <span className="text-muted fz-13">—</span>
												}
											</td>
										</tr>
									)
								})}
								{filtered.length === 0 &&
									<tr>
										<td colSpan="7" className="text-center text-muted py-5">{t('list_empty')}</td>
									</tr>
								}
							</tbody>
							<tfoot>
								{filtered.length > 0 && (
									<tr className="tr-bold table-secondary">
										<th colSpan="3">{t('total')}: {filtered.length}</th>
										<th className="text-end text-nowrap text-danger">
											{formatMoney(filtered.reduce((s, i) => s + Number(i.debt_amount), 0))}
										</th>
										<th colSpan="3"></th>
									</tr>
								)}
							</tfoot>
						</table>
					</div>
				</div>
			</div>

			{activeClient &&
				<Modal show={true} animation={false} centered size="lg"
					onHide={() => setActiveClient(null)}>
					<Modal.Header closeButton>
						<Modal.Title>
							{activeClient.client_name}
							<span className="text-muted fz-14 ms-2">— {activeClient.client_phone}</span>
							<span className="ms-3 text-danger fw-semibold fz-14">
								{formatMoney(activeClient.debt_amount)} so'm
							</span>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<form onSubmit={saveNote} className="mb-4">
							<label className="form-label fw-semibold">{t('add_note')}</label>
							<textarea
								className="form-control mb-2"
								rows={3}
								placeholder={t('note') + '...'}
								value={noteText}
								onChange={e => setNoteText(e.target.value)}
								required
							/>
							<div className="d-flex align-items-end gap-3">
								<div>
									<label className="form-label mb-1 fz-13 text-muted">
										{t('next_contact_date')} <span className="text-danger">*</span>
									</label>
									<div className="calendar-input">
										<DatePicker
											className="form-control"
											dateFormat="dd.MM.yyyy"
											minDate={new Date()}
											selected={nextContactDate}
											onChange={date => setNextContactDate(date)}
											placeholderText={t('next_contact_date')}
										/>
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
								<button
									type="submit"
									className="btn btn-primary"
									disabled={saving || !noteText.trim() || !nextContactDate}>
									{t('save_note')}
								</button>
							</div>
						</form>

						<div>
							<label className="form-label fw-semibold">{t('contact_history')}</label>
							{notes.length === 0
								? <p className="text-muted fz-13">{t('no_notes_yet')}</p>
								: <div className="d-flex flex-column gap-2" style={{ maxHeight: 380, overflowY: 'auto' }}>
									{notes.map((n, i) => (
										<div key={i} className="p-3 rounded"
											style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
											<div className="d-flex justify-content-between align-items-start mb-1">
												<span className="fw-semibold fz-13 text-secondary">{n.created_by}</span>
												<div className="text-end">
													<div className="text-muted fz-12">
														{formatDate(n.created_at, 'dd.MM.yyyy HH:mm')}
													</div>
													{n.next_contact_date &&
														<div className="fz-12 text-primary">
															{t('next_contact_date')}: {formatDate(n.next_contact_date, 'dd.MM.yyyy')}
														</div>
													}
												</div>
											</div>
											<p className="mb-0 fz-14">{n.note}</p>
										</div>
									))}
								</div>
							}
						</div>
					</Modal.Body>
				</Modal>
			}
		</>
	)
}

export default DebtMonitoring
